import { differenceInCalendarDays, isBefore } from 'date-fns';
import type { PeticePeticeDto } from '@gov-nx/api';
import {
	ePetitionCloseAndSubmitQuery,
	ePetitionCloseQuery,
	ePetitionSubmitQuery,
	usePoMutation,
} from '@gov-nx/api';
import { useIdentity } from '@gov-nx/auth/common';
import type { Nullable } from '@gov-nx/core/types';
import { EPetitionStatus } from '@gov-nx/module/page';
import { today } from '@gov-nx/utils/common';
import { useServiceAuthorization } from '../Autorizace/useServiceAuthorization';

export const AUTHORIZATION_SERVICE_NAME_EPETICE_PODAT = 'PODANI_EPETICE_PODAT';

export interface PetitionCloseSubmitProps {
	petition: Nullable<PeticePeticeDto>;
	onCloseSuccess: () => void;
	onCloseError: () => void;
	onSubmitAndClosedSuccess: (polozkaAutorizaceId?: string) => void;
	onSubmitAndClosedError: () => void;
	onSubmitSuccess: (polozkaAutorizaceId?: string) => void;
	onSubmitError: () => void;
}

export const usePetitionCloseSubmit = ({
	petition,
	onCloseSuccess,
	onCloseError,
	onSubmitAndClosedSuccess,
	onSubmitAndClosedError,
	onSubmitSuccess,
	onSubmitError,
}: PetitionCloseSubmitProps) => {
	const { isDataBoxLoginType } = useIdentity();

	const {
		isLowAuthorization: isLowAuthorizationPodat,
		isAuthorizationEnforced: isAuthorizationEnforcedPodat,
	} = useServiceAuthorization(AUTHORIZATION_SERVICE_NAME_EPETICE_PODAT);

	const closeQuery = usePoMutation({
		mutationKey: ['e-petition-close', petition?.id],
		mutationFn: ePetitionCloseQuery,
		onSuccess: () => onCloseSuccess && onCloseSuccess(),
		onError: () => onCloseError && onCloseError(),
	});

	const submitAndCloseQuery = usePoMutation({
		mutationKey: ['e-petition-close-submit', petition?.id],
		mutationFn: ePetitionCloseAndSubmitQuery,
		onSuccess: (response) =>
			onSubmitAndClosedSuccess &&
			onSubmitAndClosedSuccess(response?.headers?.['x-autorizaceid']),
		onError: () => onSubmitAndClosedError && onSubmitAndClosedError(),
	});

	const submitQuery = usePoMutation({
		mutationKey: ['e-petition-submit', petition?.id],
		mutationFn: ePetitionSubmitQuery,
		onSuccess: (response) =>
			onSubmitSuccess && onSubmitSuccess(response?.headers?.['x-autorizaceid']),
		onError: () => onSubmitError && onSubmitError(),
	});

	const date = new Date();
	const submitDate = petition?.datumPodaniDo;

	const isBeforeSubmitDate = (): boolean => {
		if (submitDate && isBefore(date, today(submitDate))) return true;
		return false;
	};

	const dayLeftUntilDeadline = (): Nullable<number> => {
		if (isBeforeSubmitDate()) {
			return Math.abs(differenceInCalendarDays(date, today(submitDate)));
		}
		return null;
	};

	const isPublic = petition?.stav === EPetitionStatus.zverejnena;
	const isClosed = petition?.stav === EPetitionStatus.uzavrena;
	const isSubmited = petition?.stav === EPetitionStatus.podana;
	const isClosedOrSubmitted =
		petition?.stav === EPetitionStatus.uzavrena ||
		petition?.stav === EPetitionStatus.podana;

	const isBeforeSubmit = isBeforeSubmitDate();
	const daysLeft = dayLeftUntilDeadline();

	const isFounder = petition?.mnouZalozene;
	const canSubmitOrClose = isFounder && isPublic && !isDataBoxLoginType;

	return {
		closeQuery,
		submitAndCloseQuery,
		submitQuery,
		canSubmitOrClose,
		isClosed,
		isSubmited,
		isFounder,
		isClosedOrSubmitted,
		isBeforeSubmit,
		daysLeft,
		isLowAuthorizationPodat,
		isAuthorizationEnforcedPodat,
	};
};
